import React, { useEffect, useState, useRef, useCallback } from 'react';
import { auth, firestore } from '../firebase';
import { onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import {
  doc,
  getDoc,
  collection,
  serverTimestamp,
  writeBatch,
  query,
  orderBy,
  where,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import { uploadToR2 } from '../uploadToR2';
import './Demo.css';

const DEMO_RING_ANIM_MS = 1400;
const NM_LOADER_RING_COUNT = 16;

// Convert a base64 dataURL (e.g. from FileReader.readAsDataURL) into a real File
// so we can hand it to uploadToR2() the same way the tryon flow does.
const dataURLtoFile = (dataUrl, filename) => {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  const ext = (mime.split('/')[1] || 'png').replace('jpeg', 'jpg');
  const safeName = filename || `avatar-${Date.now()}.${ext}`;
  return new File([u8arr], safeName, { type: mime });
};

const Demo = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Demo state
  const [previewSrc, setPreviewSrc] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  // galleryImages is now driven by Firestore (the avatar images coming back from
  // nano-banana-pro). Each entry: { id, src, isLoading, isFailed, prompt }.
  const [galleryImages, setGalleryImages] = useState([]);
  const [pendingImages, setPendingImages] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [imageSelectionOrder, setImageSelectionOrder] = useState(new Map()); // Map: imageId -> selectionNumber
  
  const dropzoneRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewBlobRevokeRef = useRef(null);
  const productFileEpochRef = useRef(0);
  const generateBtnRef = useRef(null);
  const pendingPickInputRef = useRef(null);
  const invisibleDropRef = useRef(null);
  const pendingFileEpochRef = useRef(0);

  const LS_KEY_IMAGE = 'tryonDemo.image';
  const LS_KEY_PROMPT = 'tryonDemo.prompt';

  // Resolve auth state once on mount
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  // Check if user is admin
  useEffect(() => {
    let isMounted = true;
    const checkAdmin = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      try {
        const settingsRef = doc(firestore, 'admin', 'settings');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const allowedEmails = settingsSnap.data().allowedEmails || [];
          if (isMounted) {
            setIsAdmin(allowedEmails.includes(currentUser.email));
          }
        } else {
          if (isMounted) setIsAdmin(false);
        }
      } catch (e) {
        console.error('Error checking admin status:', e);
        if (isMounted) {
          setIsAdmin(false);
          setError('Fehler beim Prüfen der Admin-Rechte');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (authReady) checkAdmin();
    return () => { isMounted = false; };
  }, [authReady, currentUser]);

  // Restore state from localStorage (preview image + prompt only — the gallery
  // is now sourced from Firestore so we never persist generated images locally)
  useEffect(() => {
    if (isAdmin) {
      try {
        const savedImage = localStorage.getItem(LS_KEY_IMAGE);
        if (savedImage) setPreviewSrc(savedImage);
        const savedPrompt = localStorage.getItem(LS_KEY_PROMPT);
        if (savedPrompt) setPrompt(savedPrompt);
      } catch (_) {}
    }
  }, [isAdmin]);

  // Live listener for the avatar images of the current user. We only show docs
  // marked with avatarImage===true so the demo gallery stays separate from the
  // regular tryon gallery. Same pattern as jewlai/tryon's useFirestoreImages.
  useEffect(() => {
    if (!isAdmin || !currentUser) {
      setGalleryImages([]);
      return undefined;
    }

    const imageCollectionRef = collection(firestore, 'users', currentUser.uid, 'image');
    const imageQuery = query(
      imageCollectionRef,
      where('avatarImage', '==', true),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      imageQuery,
      (snapshot) => {
        const next = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.hidden === true) return;
          const processedUrl = data.processedImageUrlR2 || null;
          const status = data.generationStatus;
          next.push({
            id: docSnap.id,
            src: processedUrl,
            isLoading: !processedUrl && status !== 'failed',
            isFailed: status === 'failed',
            prompt: data.prompt || null,
          });
        });
        setGalleryImages(next);
      },
      (err) => {
        console.error('Error in Firestore listener (avatar image collection):', err);
      },
    );

    return () => unsubscribe();
  }, [isAdmin, currentUser]);

  const handleSignIn = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (e) {
      setError(e.message || 'Login fehlgeschlagen');
    }
  };

  const revokePreviewBlob = () => {
    if (previewBlobRevokeRef.current) {
      try {
        URL.revokeObjectURL(previewBlobRevokeRef.current);
      } catch (_) {}
      previewBlobRevokeRef.current = null;
    }
  };

  const handleFiles = (fileList) => {
    const file = fileList && fileList[0];
    // Do not require size > 0: macOS Fotos / iCloud liefert oft 0 bis die Datei bereitsteht
    if (!file) return;

    revokePreviewBlob();
    let blobUrl = null;
    try {
      blobUrl = URL.createObjectURL(file);
      previewBlobRevokeRef.current = blobUrl;
      setPreviewSrc(blobUrl);
    } catch (_) {
      previewBlobRevokeRef.current = null;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreviewSrc(dataUrl);
      try {
        localStorage.setItem(LS_KEY_IMAGE, dataUrl);
      } catch (_) {}
      requestAnimationFrame(() => {
        revokePreviewBlob();
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.onerror = () => {
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    try {
      reader.readAsDataURL(file);
    } catch (_) {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  /** macOS Fotomediathek: `change` kann feuern bevor `input.files` befüllt ist — mehrfach versuchen, nur einmal verarbeiten. */
  const handleProductFileInputChange = useCallback((e) => {
    const input = e.currentTarget;
    const epoch = ++productFileEpochRef.current;
    let consumed = false;

    const tryConsume = () => {
      if (epoch !== productFileEpochRef.current || consumed) return;
      const files = input.files;
      if (files && files.length > 0) {
        consumed = true;
        handleFilesRef.current(files);
      }
    };

    tryConsume();
    queueMicrotask(tryConsume);
    requestAnimationFrame(tryConsume);
    setTimeout(tryConsume, 0);
    setTimeout(tryConsume, 50);
    setTimeout(tryConsume, 200);
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.style.borderColor = '#d96a93';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.style.borderColor = '#fce4ec';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current) {
      dropzoneRef.current.style.borderColor = '#fce4ec';
    }
    handleFiles(e.dataTransfer.files);
  };

  const handleClearImage = (e) => {
    e.stopPropagation();
    revokePreviewBlob();
    setPreviewSrc(null);
    try {
      localStorage.removeItem(LS_KEY_IMAGE);
    } catch (_) {}
  };

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
    try { localStorage.setItem(LS_KEY_PROMPT, e.target.value); } catch (_) {}
  };

  // Send the selected dropdown image(s) + prompt to Firebase, mirroring the
  // jewlai/tryon flow but writing to the pinkcatai users/{uid}/image collection
  // with an avatarImage marker. The deployed image_url_created cloud function
  // picks up the new doc, runs nano-banana-pro, and writes processedImageUrlR2
  // back. Our Firestore listener above then renders the result in the gallery.
  const handleGenerate = async () => {
    if (!currentUser) {
      setError('Bitte zuerst einloggen.');
      return;
    }
    if (imageSelectionOrder.size === 0) {
      setError('Bitte mindestens ein Bild im Dropdown auswählen.');
      return;
    }

    setError(null);
    setIsGenerating(true);

    // Order the selected images by their selection number so the user's order
    // is preserved in Firestore (newer createdAt wins, but we still upload in
    // selection order for predictable behaviour).
    const orderedSelection = pendingImages
      .filter((img) => imageSelectionOrder.has(img.id))
      .sort(
        (a, b) => imageSelectionOrder.get(a.id) - imageSelectionOrder.get(b.id),
      );

    const finalPrompt = (prompt || '').trim();

    try {
      const imageCollectionRef = collection(
        firestore,
        'users',
        currentUser.uid,
        'image',
      );
      const batch = writeBatch(firestore);

      for (const pending of orderedSelection) {
        // pending.src is a base64 data URL produced by FileReader during upload.
        const file = dataURLtoFile(pending.src, `avatar-${pending.id}.png`);
        const r2ImageUrl = await uploadToR2(file);
        if (!r2ImageUrl) {
          throw new Error('R2 upload failed');
        }

        const imageDocRef = doc(imageCollectionRef);
        batch.set(imageDocRef, {
          imageUrl: r2ImageUrl,
          styleImageUrl: null,
          prompt: finalPrompt,
          avatarImage: true,
          imageType: 'avatar',
          aspect_ratio: '2:3',
          size: '2K',
          output_format: 'jpeg',
          createdAt: serverTimestamp(),
          processedAt: null,
        });
      }

      await batch.commit();
    } catch (e) {
      console.error('Error during avatar generation upload:', e);
      setError(e.message || 'Generation failed.');
    } finally {
      setIsGenerating(false);
    }
  };

  const isImageLikeFile = (file) => {
    if (!file) return false;
    if (file.type && file.type.startsWith('image/')) return true;
    if (/\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || '')) return true;
    if (!file.type && (file.name === '' || !file.name)) return true;
    return false;
  };

  // Manual drag-into-gallery is intentionally disabled: the gallery now reflects
  // AI output from Firestore. We keep no-op handlers so we don't break the
  // existing drop targets in the JSX without restructuring the layout.
  const handleGalleryDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleGalleryDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Hide the avatar image from this user's view by flagging it in Firestore.
  // The onSnapshot listener filters out hidden docs.
  const removeGalleryImage = async (imageId) => {
    if (!currentUser) return;
    try {
      const ref = doc(firestore, 'users', currentUser.uid, 'image', imageId);
      await updateDoc(ref, { hidden: true });
    } catch (e) {
      console.error('Error hiding avatar image:', e);
    }
  };

  const handleInvisibleDropFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const fileArray = Array.from(fileList).filter(isImageLikeFile);
    if (fileArray.length === 0) return;

    const newImages = [];
    const baseTimestamp = Date.now();

    fileArray.forEach((file, index) => {
      const reader = new FileReader();
      const uniqueId = `${baseTimestamp}-${index}-${Math.random().toString(36).substr(2, 9)}`;
      reader.onload = () => {
        const dataUrl = reader.result;
        newImages.push({ id: uniqueId, src: dataUrl });

        if (newImages.length === fileArray.length) {
          setPendingImages((prev) => [...prev, ...newImages]);
          if (pendingPickInputRef.current) pendingPickInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleInvisibleDropFilesRef = useRef(handleInvisibleDropFiles);
  handleInvisibleDropFilesRef.current = handleInvisibleDropFiles;

  const handlePendingPickInputChange = useCallback((e) => {
    const input = e.currentTarget;
    const epoch = ++pendingFileEpochRef.current;
    let consumed = false;

    const tryConsume = () => {
      if (epoch !== pendingFileEpochRef.current || consumed) return;
      const files = input.files;
      if (files && files.length > 0) {
        consumed = true;
        handleInvisibleDropFilesRef.current(files);
      }
    };

    tryConsume();
    queueMicrotask(tryConsume);
    requestAnimationFrame(tryConsume);
    setTimeout(tryConsume, 0);
    setTimeout(tryConsume, 50);
    setTimeout(tryConsume, 200);
  }, []);

  const handleInvisibleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInvisibleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleInvisibleDropFiles(e.dataTransfer.files);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const togglePendingImageSelection = (imageId) => {
    setImageSelectionOrder(prev => {
      const newMap = new Map(prev);
      if (newMap.has(imageId)) {
        // Remove this image and renumber all higher numbers
        const removedNumber = newMap.get(imageId);
        newMap.delete(imageId);
        // Decrease all numbers higher than the removed one
        for (const [id, num] of newMap.entries()) {
          if (num > removedNumber) {
            newMap.set(id, num - 1);
          }
        }
      } else {
        // Add with next number
        const maxNumber = newMap.size > 0 ? Math.max(...newMap.values()) : 0;
        newMap.set(imageId, maxNumber + 1);
      }
      return newMap;
    });
  };

  const removePendingImage = (imageId) => {
    const updatedPending = pendingImages.filter(img => img.id !== imageId);
    setPendingImages(updatedPending);
    setImageSelectionOrder(prev => {
      const newMap = new Map(prev);
      if (newMap.has(imageId)) {
        const removedNumber = newMap.get(imageId);
        newMap.delete(imageId);
        // Decrease all numbers higher than the removed one
        for (const [id, num] of newMap.entries()) {
          if (num > removedNumber) {
            newMap.set(id, num - 1);
          }
        }
      }
      return newMap;
    });
  };

  if (loading) return <div style={{ padding: 16 }}>Lade…</div>;
  
  if (!currentUser) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ marginBottom: 12 }}>Bitte einloggen, um die Demo zu sehen.</div>
        <button onClick={handleSignIn}>Mit Google einloggen</button>
        {error && <div style={{ color: 'red', marginTop: 8 }}>Fehler: {error}</div>}
      </div>
    );
  }
  
  if (!isAdmin) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'red' }}>Keine Admin-Berechtigung für {currentUser.email}</div>
      </div>
    );
  }

  // Render a single gallery tile. Handles three states:
  // 1) succeeded: show the processed avatar from R2
  // 2) loading: a pink placeholder spinner while nano-banana-pro is working
  // 3) failed: a small error tile so the user knows what happened
  const renderGalleryItem = (img) => (
    <div key={img.id} className="demo-gallery-image">
      {img.src ? (
        <img src={img.src} alt="Gallery" />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: img.isFailed
              ? 'rgba(229, 62, 62, 0.08)'
              : '#fce4ec',
            color: img.isFailed ? '#E53E3E' : '#8b4d6a',
            fontSize: 13,
            fontWeight: 500,
            textAlign: 'center',
            padding: 12,
            boxSizing: 'border-box',
          }}
        >
          {img.isFailed ? 'Generation failed' : 'Generating…'}
        </div>
      )}
      <button
        className="demo-gallery-remove"
        onClick={() => removeGalleryImage(img.id)}
        title="Bild entfernen"
      >
        ×
      </button>
    </div>
  );

  return (
    <main className="demo-page">
      <div className="demo-main-content">
        {/* Left Panel - Upload & Controls */}
        <div className="demo-left-panel">
          <div className="demo-card-wrapper">
            <section className="demo-card" aria-label="Tryon Demo">
              <div 
                ref={dropzoneRef}
                className="demo-dropzone" 
                tabIndex="0" 
                role="button" 
                aria-label="Bild hierher ziehen oder klicken, um ein Bild auszuwählen"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input 
                  ref={fileInputRef}
                  className="demo-file-input" 
                  type="file" 
                  accept="image/*,.heic,.heif,image/heic,image/heif"
                  aria-hidden="true"
                  onChange={handleProductFileInputChange}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                />
                {previewSrc ? (
                  <div className="demo-preview">
                    <img src={previewSrc} alt="Vorschau" />
                    <button 
                      className="demo-clear" 
                      title="Bild entfernen" 
                      aria-label="Bild entfernen"
                      onClick={handleClearImage}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="demo-placeholder">
                    <span>drag & drop</span>
                    <span className="demo-or"></span>
                  </div>
                )}
              </div>

              <textarea 
                className="demo-prompt" 
                rows="2" 
                placeholder=""
                value={prompt}
                onChange={handlePromptChange}
              />
            </section>
            <button 
              ref={generateBtnRef}
              className="demo-generate" 
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Image'}
            </button>
            {isGenerating && (
              <div className="demo-generate-loader">
                <div
                  className="demo-nm-loader"
                  style={{ '--demo-gen-ms': `${DEMO_RING_ANIM_MS}ms` }}
                  role="progressbar"
                  aria-valuetext="Generierung läuft"
                  aria-busy="true"
                >
                  <div className="demo-nm-loader__container">
                    {Array.from({ length: NM_LOADER_RING_COUNT }, (_, idx) => (
                      <div
                        key={idx}
                        className="demo-nm-loader__item"
                        style={{ '--i': idx + 1 }}
                        aria-hidden="true"
                      />
                    ))}
                    <span className="demo-generate-loader__loading-text" aria-hidden="true">
                      Loading
                    </span>
                  </div>
                </div>
                <span className="demo-generate-loader__label">Bibbidi Bobbidi Boo</span>
              </div>
            )}

            {/* Dropdown vor der mobilen Galerie: zuerst vorbereiten, darunter „your images…“ */}
            <div className="demo-dropdown-wrapper">
              <div 
                className="demo-dropdown-trigger"
                onClick={toggleDropdown}
              >
                <span>
                  {pendingImages.length > 0 
                    ? `${imageSelectionOrder.size} von ${pendingImages.length} ausgewählt` 
                    : 'Bilder vorbereiten'}
                </span>
                <span className={`demo-dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>▼</span>
              </div>
              
              {dropdownOpen && (
                <div 
                  ref={invisibleDropRef}
                  className="demo-dropdown-content"
                  onDragOver={handleInvisibleDragOver}
                  onDrop={handleInvisibleDrop}
                >
                  {pendingImages.length === 0 ? (
                    <div className="demo-dropdown-empty demo-dropdown-empty--pickable">
                      <input
                        ref={pendingPickInputRef}
                        type="file"
                        accept="image/*,.heic,.heif,image/heic,image/heif"
                        multiple
                        className="demo-pending-file-input"
                        aria-label="Bilder auswählen oder hierher ziehen"
                        onChange={handlePendingPickInputChange}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                      <span className="demo-dropdown-empty-label">
                        Ziehe Bilder hierher oder tippe / klicke, um Fotos zu wählen
                      </span>
                    </div>
                  ) : (
                    <div className="demo-dropdown-images">
                      {pendingImages.map((img) => {
                        const selectionNumber = imageSelectionOrder.get(img.id);
                        const isSelected = selectionNumber !== undefined;
                        return (
                          <div 
                            key={img.id} 
                            className={`demo-dropdown-image-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => togglePendingImageSelection(img.id)}
                          >
                            <img src={img.src} alt="Vorschau" className="demo-dropdown-thumbnail" />
                            {isSelected && (
                              <div className="demo-selection-number">
                                {selectionNumber}
                              </div>
                            )}
                            <button
                              className="demo-dropdown-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePendingImage(img.id);
                              }}
                              title="Entfernen"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Gallery below dropdown on mobile; hidden on desktop (right panel shows gallery) */}
            <div 
              className="demo-gallery-inline"
              onDragOver={handleGalleryDragOver}
              onDrop={handleGalleryDrop}
            >
              {galleryImages.length === 0 ? (
                <div className="demo-gallery-empty">
                  <div className="demo-gallery-empty-text">
                    your images will be displayed here
                  </div>
                </div>
              ) : (
                <div className="demo-gallery-images">
                  {galleryImages.map(renderGalleryItem)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Gallery (desktop only) */}
        <div 
          className="demo-right-panel demo-gallery-desktop"
          onDragOver={handleGalleryDragOver}
          onDrop={handleGalleryDrop}
        >
          <div className="demo-gallery-container">
            {galleryImages.length === 0 ? (
              <div className="demo-gallery-empty">
                <div className="demo-gallery-empty-text">
                  your images will be displayed here
                </div>
              </div>
            ) : (
              <div className="demo-gallery-images">
                {galleryImages.map(renderGalleryItem)}
              </div>
            )}
          </div>
        </div>
      </div>
      {error && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#E53E3E',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
          }}
        >
          {error}
        </div>
      )}
    </main>
  );
};

export default Demo;

